import React, { useState, useEffect } from 'react';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';

const ConfirmationDialog = ({
  open,
  options: builtOptions,
  onCancel,
  onConfirm,
  onValue,
  onClose,
}) => {
  const [options, setOptions] = useState(builtOptions);

  // Sync local state with prop changes
  useEffect(() => {
    setOptions(builtOptions);
  }, [builtOptions]);

  const {
    title,
    description,
    content,
    Form,
    FormProps,
    confirmationText,
    cancellationText,
    dialogProps,
    dialogActionsProps,
    dialogActionsContent,
    confirmationButtonProps,
    cancellationButtonProps,
    titleProps,
    contentProps,
    allowClose,
    confirmationKeyword,
    confirmationKeywordTextFieldProps,
    hideCancelButton,
    buttonOrder,
    valueButtons,
    acknowledgement,
    acknowledgementFormControlLabelProps,
    acknowledgementCheckboxProps,
  } = options;

  const [confirmationKeywordValue, setConfirmationKeywordValue] = useState("");
  const [isAcknowledged, setIsAcknowledged] = useState(false);

  const confirmationButtonDisabled = Boolean(
    (confirmationKeyword && confirmationKeywordValue !== confirmationKeyword) ||
    (acknowledgement && !isAcknowledged),
  );

  const acknowledgeCheckbox = (
    <>
      {acknowledgement && (
        <FormControlLabel
          {...acknowledgementFormControlLabelProps}
          control={
            <Checkbox
              {...acknowledgementCheckboxProps}
              value={isAcknowledged}
              onChange={(_, value) => setIsAcknowledged(value)}
            />
          }
          label={acknowledgement}
        />
      )}
    </>
  );

  const confirmationContent = (
    <>
      {confirmationKeyword && (
        <TextField
          onChange={(e) => setConfirmationKeywordValue(e.target.value)}
          value={confirmationKeywordValue}
          fullWidth
          {...confirmationKeywordTextFieldProps}
        />
      )}
    </>
  );

  const dialogActions = buttonOrder.reduce((acc, buttonType) => {
    if (buttonType === "cancel") {
      acc.push(
        !hideCancelButton && (
          <Button
            key="cancel"
            id="cancel-button"
            {...cancellationButtonProps}
            onClick={onCancel}
          >
            {cancellationText}
          </Button>
        )
      );
      return acc;
    }

    if (buttonType === "value") {
      if (valueButtons.length > 0) {
        acc.push(...valueButtons.map((props, i) => {
          return (
            <Button
              key={`value-button-${i}`}
              color={props.color}
              {...cancellationButtonProps}
              onClick={() => onValue(props.value)}
            >
              {props.text}
            </Button>
          );
        }));
      }
      return acc;
    }

    if (buttonType === "confirm") {
      acc.push(
        <Button
          key="confirm"
          id="confirm-button"
          color="primary"
          disabled={confirmationButtonDisabled}
          {...confirmationButtonProps}
          onClick={onConfirm}
        >
          {confirmationText}
        </Button>
      );
      return acc;
    }

    throw new Error(
      `Supported button types are only "confirm" "value" and "cancel", got: ${buttonType}`,
    );
  }, []);

  return (
    <Dialog
      disableRestoreFocus
      fullWidth
      {...dialogProps}
      open={open}
      onClose={allowClose ? onClose : null}
    >
      {title && <DialogTitle {...titleProps}>{title}</DialogTitle>}
      {Form ? (
        <DialogContent {...contentProps}>
          <Form { ...FormProps } options={options} setOptions={setOptions} />
        </DialogContent>
      ) : content ? (
        <DialogContent {...contentProps}>
          {content}
          {confirmationContent}
          {acknowledgeCheckbox}
        </DialogContent>
      ) : description ? (
        <DialogContent {...contentProps}>
          <DialogContentText>{description}</DialogContentText>
          {confirmationContent}
          {acknowledgeCheckbox}
        </DialogContent>
      ) : (
        (confirmationKeyword || acknowledgeCheckbox) && (
          <DialogContent {...contentProps}>
            {confirmationContent}
            {acknowledgeCheckbox}
          </DialogContent>
        )
      )}
      <DialogActions {...dialogActionsProps}>
        {dialogActionsContent}
        {dialogActions}
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
